import { useTranslation } from "next-i18next";

import { Price } from "@/Apollo/schema";
import { IconProfileProBuyer } from "@/assets/icons/figma/IconProfileProBuyer";
import { cn } from "@/common/utils/css.utils";
import { ProBuyerPrice } from "@/features/pro-buyer-price/data/pro-buyer-price-client";
import { InfoLabel } from "@/ui/cva/InfoLabel";

/**
 * WHO-5743. The "Pro Buyer-prijs" label. Square on the search card image,
 * Rounded 4px above the PDP price; Small Light Brand on both. The program
 * never shows a strikethrough, so nothing here draws one. WHO-5953: carries the
 * account-tag icon, the same anchor as the search strip.
 */
export const ProBuyerPriceLabel = ({
  corners = "square",
  className,
}: {
  corners?: "square" | "rounded";
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <InfoLabel
      color="brand"
      variant="light"
      size="small"
      corners={corners}
      leftIcon={<IconProfileProBuyer aria-hidden className="size-4 shrink-0" />}
      className={cn("min-w-0 max-w-fit whitespace-nowrap", className)}
      data-testid="pro-buyer-price-label"
    >
      {t("common:pro-buyer-price-label", "Pro Buyer price")}
    </InfoLabel>
  );
};

type ProBuyerPdpPriceProps = {
  proBuyerPrice: ProBuyerPrice;
  /** The regular buy-now price: the reference line, never struck through. */
  regularPrice: Pick<Price, "amount" | "currency"> | null | undefined;
};

/**
 * PDP block for a Pro Buyer: label, the Pro Buyer price, then one line
 * "Gewone prijs € 1.850,- · 15% korting van de verkoper" with the discount
 * part in brand-600 bold. With a seller sale this replaces the whole sale
 * block: the current price is the reference, no old price, no badge.
 */
export const ProBuyerPdpPrice = ({
  proBuyerPrice,
  regularPrice,
}: ProBuyerPdpPriceProps) => {
  const { t } = useTranslation();

  return (
    <span
      className="flex flex-col items-start space-y-1"
      data-testid="pro-buyer-price-pdp"
    >
      <ProBuyerPriceLabel corners="rounded" />
      <span
        data-testid="pdp.price.item"
        className="font-title font-bold text-black"
      >
        {t("common:common-price", { price: proBuyerPrice.price })}
      </span>
      <span className="font-body text-sm text-grey-500">
        {regularPrice && (
          <>
            {t("common:pro-buyer-price-regular", {
              defaultValue: "Regular price {{price}}",
              price: t("common:common-price", { price: regularPrice }),
            })}
            {" · "}
          </>
        )}
        <span className="font-bold text-brand-600">
          {t("common:pro-buyer-price-seller-discount", {
            defaultValue: "{{pct}}% discount from the seller",
            pct: proBuyerPrice.pct,
          })}
        </span>
      </span>
    </span>
  );
};

/** Buyer side of the chat product card: label and Pro Buyer price. */
export const ProBuyerChatPrice = ({
  proBuyerPrice,
}: {
  proBuyerPrice: ProBuyerPrice;
}) => {
  const { t } = useTranslation();

  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      data-testid="pro-buyer-price-chat"
    >
      <ProBuyerPriceLabel />
      <span>{t("common:common-price", { price: proBuyerPrice.price })}</span>
    </span>
  );
};

/**
 * Seller side of the chat product card, under the regular price:
 * "Pro Buyer-prijs € 1.572,- (jouw korting 15%)".
 */
export const ProBuyerChatCounterpartLine = ({
  proBuyerPrice,
}: {
  proBuyerPrice: ProBuyerPrice;
}) => {
  const { t } = useTranslation();

  return (
    <span
      className="font-body text-sm text-brand-600"
      data-testid="pro-buyer-price-chat-counterpart"
    >
      {t("common:pro-buyer-price-chat-counterpart", {
        defaultValue: "Pro Buyer price {{price}} (your discount {{pct}}%)",
        price: t("common:common-price", { price: proBuyerPrice.price }),
        pct: proBuyerPrice.pct,
      })}
    </span>
  );
};
